import React, { type ReactElement, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Table from 'atom-components/table-outdated/table';
import { getQuestionTypeClassName } from 'feature-components/reports/individual-report-shared/data-process/getQuestionTypeClassName';
import type { Statistics } from 'feature-components/reports/item-assessment-report';
import useColumns from 'feature-components/reports/item-assessment-report/column-definitions';
import type { Sort } from 'feature-components/reports/item-assessment-report/controls/select-sort-type';
import { useQuestionLink } from 'feature-components/reports/item-assessment-report/item-assessment-report.helpers';
import ItemPerformanceChart from 'feature-components/reports/item-assessment-report/item-performance-chart';
import type { BaseArea } from 'services/assist-api/entities/area/areas';
import type { BaseReferenceDataNational } from 'services/assist-api/entities/reference-data-national/reference-data-nationals';
import {
  type BaseTestItem,
  type Category,
  QuestionType
} from 'services/assist-api/entities/test-item/test-items';
import type { TestType } from 'services/assist-api/entities/test/tests';

export type Props = {
  area: BaseArea;
  testType: TestType | undefined;
  testItems: BaseTestItem[];
  responseItems: {
    id: number;
    isCorrect: boolean;
    testItem: { id: number; identifier: string };
  }[];
  referenceData: BaseReferenceDataNational[];
  isReferenceDataVisible: boolean;
  minItemScaleScore: number;
  maxItemScaleScore: number;
  iconWidth: number;
  itemSorting: Sort;
  minEncounters?: number;
  minPercentageCorrect?: number;
  onClickCategory: (c: Category) => void;
};

export default function ItemAssessmentReport(props: Props): ReactElement {
  const {
    area,
    testType,
    testItems,
    responseItems,
    referenceData,
    itemSorting,
    isReferenceDataVisible,
    minItemScaleScore,
    maxItemScaleScore,
    minEncounters,
    minPercentageCorrect,
    iconWidth,
    onClickCategory
  } = props;

  const { categoryId } = useParams();
  const generateQuestionLink = useQuestionLink();

  const questions: Statistics[] = useMemo(
    () =>
      testItems
        .filter(({ type }) => type !== QuestionType.CustomQuizEvaluation) // filter out items that should not make it into report
        .filter((item) =>
          categoryId ? item.categories.find(({ id }) => `${id}` === categoryId) : true
        )
        .map((q) => {
          const itemAnswers = responseItems?.filter((item) => item.testItem.id === q.id) || [];
          const correctAnswers = itemAnswers?.filter((a) => a.isCorrect) || [];

          const percentageCorrect = (correctAnswers.length / itemAnswers.length) * 100 || 0;
          const percentageNational = referenceData.find((data) => data.testItem.id === q.id);
          const questionType = q.metadatas?.find(
            (meta) => meta.name.toLowerCase() === 'question type'
          );
          return {
            ...q,
            ...(questionType
              ? {
                  ...questionType,
                  className: getQuestionTypeClassName(questionType?.value || '')
                }
              : {}),
            numResponses: itemAnswers?.length ?? 0,
            numResponsesCorrect: correctAnswers?.length ?? 0,
            percentageCorrect: percentageCorrect.toFixed(2),
            percentageNational: percentageNational?.percentage.toFixed(0) || 'N/A',
            testItemLink: generateQuestionLink(q.id)
          };
        })
        .filter((item) => item.numResponses >= minEncounters),
    [categoryId, generateQuestionLink, minEncounters, referenceData, responseItems, testItems]
  );

  const columns = useColumns({
    areaIdentifier: area.identifier,
    canHaveStyledQuestionType: !!questions.find((q) => !!q.questionType?.className),
    showReferenceData: !!referenceData.length && isReferenceDataVisible,
    testType
  });

  return (
    <>
      <ItemPerformanceChart
        area={area}
        questions={questions}
        minPercentageCorrect={minPercentageCorrect}
        minItemScaleScore={minItemScaleScore}
        maxItemScaleScore={maxItemScaleScore}
        itemSorting={itemSorting}
        iconWidth={iconWidth}
        onClickCategory={onClickCategory}
      />
      <Table columns={columns} data={questions} className="table-bordered" pageSize={50} />
    </>
  );
}
